import { PageIcon } from "@/components/PageIcon";
import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { ClipboardList, X, Loader2, CheckCircle2, Truck, CalendarClock, AlertTriangle, ChevronRight, Search, Rows3, Layers, PackagePlus } from "lucide-react";
import DataTable from "@/components/DataTable";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const STATUSES = ["Approved", "Installed - Finance Requested", "Requested", "Fulfilled", "Rejected"];
const STATUS_CLS = {
  Approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Installed - Finance Requested": "bg-amber-100 text-amber-800 border-amber-300",
  Requested: "bg-slate-100 text-slate-600 border-slate-200",
  Fulfilled: "bg-indigo-50 text-indigo-700 border-indigo-200",
  Rejected: "bg-rose-50 text-rose-700 border-rose-200",
};

function StatusPill({ status }) {
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap ${STATUS_CLS[status] || STATUS_CLS.Requested}`}>{status}</span>;
}
function LowStockPill({ className = "" }) {
  return <span className={`inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700 ${className}`} data-testid="low-stock-pill"><AlertTriangle className="w-3 h-3" /> Low stock</span>;
}

const columns = [
  { key: "request_id", label: "ID", mono: true },
  { key: "product", label: "Product", strong: true, truncate: true },
  { key: "requested_for", label: "Requested For", truncate: true },
  { key: "stock_level", label: "Stock Level", align: "right", render: (v, row) => (
    <span className={`inline-flex items-center gap-2 justify-end ${row.low_stock ? "text-rose-600 font-bold" : ""}`}>
      {v}{row.low_stock && <LowStockPill />}
    </span>
  ) },
  { key: "qty_requested", label: "Quantity Requested", align: "right" },
  { key: "qty_fulfilled", label: "Quantity Fulfilled", align: "right" },
  { key: "qty_required", label: "Quantity Required", align: "right" },
  { key: "status", label: "Status", render: (v) => <StatusPill status={v} /> },
];

const dt = (s) => (s ? new Date(s).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const dstr = (s) => (s ? new Date(s + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" }) : "—");

function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-sm text-slate-800 mt-0.5">{children ?? <span className="text-slate-300">—</span>}</div>
    </div>
  );
}
function Chip({ children }) {
  return <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-sm text-slate-700">{children}</span>;
}

function RequestDrawer({ id, onClose, onChanged }) {
  const { can } = useAuth();
  const canWrite = can("stock.adjust") || can("stock.manage");
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [srcWh, setSrcWh] = useState("");
  const load = () => api.get(`/product-requests/${id}`).then((r) => setDoc(r.data)).catch(() => setDoc(false));
  useEffect(() => { setDoc(null); load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => {
    api.get("/stock/locations").then((r) => {
      const whs = (r.data.items || []).filter((l) => ["warehouse", "showroom", "depot"].includes(l.type));
      setWarehouses(whs);
      const main = whs.find((w) => w.code === "MAIN") || whs[0];
      if (main) setSrcWh(main.location_id);
    }).catch(() => {});
  }, []);

  const act = async (payload) => {
    setBusy(true);
    try {
      const r = await api.patch(`/product-requests/${id}`, payload);
      toast.success(r?.data?.allocation_note || "Request updated");
      await load(); onChanged && onChanged();
    }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not update"); }
    finally { setBusy(false); }
  };

  const requestStock = async (locId) => {
    setBusy(true);
    try {
      const r = await api.post(`/product-requests/${id}/request-stock`, { location_id: locId || undefined });
      toast.success(r?.data?.note || "Requested from warehouse");
      await load(); onChanged && onChanged();
    }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not raise request"); }
    finally { setBusy(false); }
  };

  const receiveStock = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/product-requests/${id}/receive-stock`);
      toast.success(r?.data?.note || "Received into warehouse");
      await load(); onChanged && onChanged();
    }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not receive"); }
    finally { setBusy(false); }
  };

  const fulfilFromWarehouse = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/product-requests/${id}/fulfil-from-warehouse`);
      toast.success(r?.data?.note || "Fulfilled from warehouse");
      await load(); onChanged && onChanged();
    }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not fulfil"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex justify-end" data-testid="product-request-drawer">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative bg-white w-full sm:w-1/2 max-w-none h-full shadow-2xl overflow-y-auto">
        {doc === null ? (
          <div className="p-10 flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /> Loading…</div>
        ) : doc === false ? (
          <div className="p-10 text-slate-500">Request not found.</div>
        ) : (
          <>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white z-10">
              <h2 className="text-lg font-bold text-slate-900">Product Request {doc.request_id}</h2>
              <div className="flex items-center gap-2">
                {canWrite && doc.low_stock && !doc.stock_requested && doc.status !== "Fulfilled" && (
                  <>
                    {warehouses.length > 1 && (
                      <select value={srcWh} onChange={(e) => setSrcWh(e.target.value)} disabled={busy}
                        className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm" data-testid="pr-warehouse-select">
                        {warehouses.map((w) => <option key={w.location_id} value={w.location_id}>{w.name}</option>)}
                      </select>
                    )}
                    <Button onClick={() => requestStock(srcWh)} disabled={busy} className="h-9 gap-1.5 bg-amber-500 hover:bg-amber-600 text-white" data-testid="pr-request-stock">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackagePlus className="w-4 h-4" />} Request from warehouse
                    </Button>
                  </>
                )}
                {canWrite && doc.stock_requested && !doc.goods_received && doc.status !== "Fulfilled" && (
                  <Button onClick={receiveStock} disabled={busy} className="h-9 gap-1.5 bg-sky-600 hover:bg-sky-700 text-white" data-testid="pr-receive-stock">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackagePlus className="w-4 h-4" />} Receive into warehouse
                  </Button>
                )}
                {canWrite && doc.stock_requested && doc.status !== "Fulfilled" && (
                  <Button onClick={fulfilFromWarehouse} disabled={busy} className="h-9 gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="pr-fulfil-warehouse">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />} Fulfil from warehouse
                  </Button>
                )}
                {canWrite && doc.status !== "Fulfilled" && (
                  <Button onClick={() => act({ mark_fulfilled: true })} disabled={busy} className="h-9 gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="pr-mark-fulfilled">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Allocate &amp; fulfil
                  </Button>
                )}
                <button onClick={onClose} className="p-1.5 rounded-full bg-slate-100 text-slate-500 hover:text-slate-800" data-testid="pr-drawer-close"><X className="w-5 h-5" /></button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {doc.low_stock && (
                <div className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800" data-testid="pr-low-stock-banner">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>Low stock — only <strong>{doc.stock_level}</strong> on hand but <strong>{doc.qty_required}</strong> required. Order or transfer before allocating.</span>
                </div>
              )}
              {doc.stock_requested && (
                <div className="flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800" data-testid="pr-stock-request-chip">
                  <PackagePlus className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>Requested <strong>{doc.stock_request_qty}</strong> from <strong>{doc.stock_request_location || "Main Warehouse"}</strong>{doc.stock_request_reserved > 0 ? ` — ${doc.stock_request_reserved} reserved on hand` : " — none in stock, warehouse to source"}.</span>
                </div>
              )}
              {doc.allocation_note && (
                <div className="flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700" data-testid="pr-alloc-note">
                  <Truck className="w-4 h-4 mt-0.5 shrink-0 text-indigo-500" /><span>{doc.allocation_note}</span>
                </div>
              )}

              <section>
                <h3 className="text-sm font-bold text-indigo-600 mb-3">Product Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Product"><Chip>{doc.product}</Chip></Field>
                  <Field label="Product Type">{doc.product_type}</Field>
                  <Field label="Stock Level"><span className={doc.low_stock ? "text-rose-600 font-bold" : ""}>{doc.stock_level}</span></Field>
                </div>
              </section>

              <section>
                <h3 className="text-sm font-bold text-indigo-600 mb-3">Request Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Requested By"><Chip>{doc.requested_by}</Chip></Field>
                  <Field label="Requested At">{dt(doc.requested_at)}</Field>
                  <Field label="Quantity Requested">{doc.qty_requested}</Field>
                  <Field label="Quantity Required">{doc.qty_required}</Field>
                  <Field label="Quantity Fulfilled">{doc.qty_fulfilled}</Field>
                  <Field label="Request Type">{doc.request_type}</Field>
                  <Field label="Status">
                    {canWrite ? (
                      <select value={doc.status} onChange={(e) => act({ status: e.target.value })} disabled={busy}
                        className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-sm" data-testid="pr-status-select">
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    ) : <StatusPill status={doc.status} />}
                  </Field>
                  <Field label="Task">{doc.task_legacy_id ? <a href={`/tasks/${doc.task_legacy_id}`} className="text-indigo-600 hover:underline" data-testid="pr-task-link"><Chip>{doc.task_name}</Chip></a> : (doc.task_name ? <Chip>{doc.task_name}</Chip> : null)}</Field>
                  <Field label="Quote">{doc.quote_name ? <Chip>{doc.quote_name}</Chip> : null}</Field>
                </div>
              </section>

              <section>
                <h3 className="text-sm font-bold text-indigo-600 mb-3">On-site (linked shifts)</h3>
                {(!doc.shifts || doc.shifts.length === 0) ? (
                  <p className="text-sm text-slate-400">No shifts scheduled yet for this job.</p>
                ) : (
                  <div className="space-y-1.5" data-testid="pr-shifts">
                    {doc.shifts.map((s, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm rounded-lg border border-slate-200 px-3 py-2">
                        <CalendarClock className="w-4 h-4 text-indigo-500 shrink-0" />
                        <span className="font-medium text-slate-700">{dstr(s.date)}{s.start_time ? ` · ${s.start_time}` : ""}</span>
                        {s.vehicle_title && <span className="text-slate-500 inline-flex items-center gap-1"><Truck className="w-3.5 h-3.5" /> {s.vehicle_title}</span>}
                        {s.shift_status && <span className="text-xs text-slate-400 ml-auto">{s.shift_status}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section>
                <h3 className="text-sm font-bold text-indigo-600 mb-3">Review Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Reviewed By">{doc.reviewed_by}</Field>
                  <Field label="Reviewed At">{dt(doc.reviewed_at)}</Field>
                  <div className="col-span-2"><Field label="Review Notes">{doc.review_notes}</Field></div>
                </div>
              </section>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function GroupedView({ onOpen }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [outstanding, setOutstanding] = useState(false);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});

  const load = useCallback(() => {
    api.get("/product-requests/grouped", { params: { q, page, page_size: 20, status, outstanding } })
      .then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0, total_pages: 1 }));
  }, [q, page, status, outstanding]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { setPage(1); }, [q, status, outstanding]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search product, customer, status…" className="pl-9 h-10" data-testid="grouped-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm" data-testid="grouped-status">
          <option value="all">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={() => setOutstanding((o) => !o)} data-testid="grouped-outstanding"
          className={`h-10 px-3 rounded-lg border text-sm font-medium ${outstanding ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-white border-slate-200 text-slate-600"}`}>
          Outstanding only
        </button>
      </div>

      {!data ? (
        <div className="p-10 flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /> Loading…</div>
      ) : data.items.length === 0 ? (
        <div className="p-10 text-center text-slate-400 border border-dashed border-slate-200 rounded-xl">No product requests found.</div>
      ) : (
        <div className="rounded-xl border border-slate-200 overflow-hidden divide-y divide-slate-100 bg-white" data-testid="grouped-list">
          {data.items.map((g) => {
            const isOpen = !!open[g.group_id];
            return (
              <div key={g.group_id} data-testid={`grouped-row-${g.group_id}`}>
                <button onClick={() => setOpen((o) => ({ ...o, [g.group_id]: !o[g.group_id] }))}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors">
                  <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-800 truncate">{g.job}</div>
                    <div className="text-xs text-slate-500">{g.lines.length} line{g.lines.length > 1 ? "s" : ""}{g.task_legacy_id ? ` · Job #${g.task_legacy_id}` : ""} · {g.request_type}</div>
                  </div>
                  {g.low_stock > 0 && <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2.5 py-0.5 text-xs font-semibold text-rose-700"><AlertTriangle className="w-3 h-3" /> {g.low_stock} low</span>}
                  {g.outstanding > 0
                    ? <span className="inline-flex items-center rounded-full border border-amber-300 bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800">{g.outstanding} outstanding</span>
                    : <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">All fulfilled</span>}
                </button>
                {isOpen && (
                  <div className="bg-slate-50/60 px-4 pb-3">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400">
                          <th className="py-2 font-semibold">Product</th>
                          <th className="py-2 font-semibold text-right">Stock</th>
                          <th className="py-2 font-semibold text-right">Req'd</th>
                          <th className="py-2 font-semibold text-right">Fulfilled</th>
                          <th className="py-2 font-semibold">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.lines.map((l) => (
                          <tr key={l.request_id} onClick={() => onOpen(l.request_id)}
                            className="border-t border-slate-200 cursor-pointer hover:bg-white" data-testid={`grouped-line-${l.request_id}`}>
                            <td className="py-2 font-medium text-slate-800">{l.product}</td>
                            <td className={`py-2 text-right ${l.low_stock ? "text-rose-600 font-bold" : "text-slate-600"}`}>{l.stock_level}{l.low_stock && <span className="ml-1 align-middle"><LowStockPill /></span>}</td>
                            <td className="py-2 text-right text-slate-600">{l.qty_required}</td>
                            <td className="py-2 text-right text-slate-600">{l.qty_fulfilled}</td>
                            <td className="py-2"><StatusPill status={l.status} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {data && data.total_pages > 1 && (
        <div className="flex items-center justify-between text-sm text-slate-500">
          <span>Page {page} of {data.total_pages} · {data.total} jobs</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
            <Button variant="outline" size="sm" disabled={page >= data.total_pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProductRequestsPage() {
  const [sp, setSp] = useSearchParams();
  const reqId = sp.get("req");
  const [refreshKey, setRefreshKey] = useState(0);
  const [grouped, setGrouped] = useState(false);
  const openReq = (rid) => { const n = new URLSearchParams(sp); n.set("req", rid); setSp(n, { replace: true }); };
  const closeDrawer = () => { const n = new URLSearchParams(sp); n.delete("req"); setSp(n, { replace: true }); };

  const toggle = (
    <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="pr-view-toggle">
      <button onClick={() => setGrouped(false)} data-testid="pr-view-lines"
        className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${!grouped ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
        <Rows3 className="w-4 h-4" /> Line items
      </button>
      <button onClick={() => setGrouped(true)} data-testid="pr-view-grouped"
        className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${grouped ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
        <Layers className="w-4 h-4" /> Group by job
      </button>
    </div>
  );

  return (
    <>
      {grouped ? (
        <div className="p-6 lg:p-8">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <div className="flex items-center gap-3">
              <PageIcon />
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Product Requests</h1>
            </div>
            {toggle}
          </div>
          <GroupedView onOpen={openReq} />
        </div>
      ) : (
        <DataTable
          key={refreshKey}
          dataset="product-requests"
          title="Product Requests"
          icon={ClipboardList}
          columns={columns}
          searchPlaceholder="Search product, customer, status…"
          rowLink={(r) => `?req=${r.request_id}`}
          headerActions={toggle}
          filters={[
            { type: "select", param: "status", label: "Status", options: STATUSES.map((s) => ({ value: s, label: s })) },
            { type: "toggle", param: "outstanding", label: "Outstanding only" },
          ]}
        />
      )}
      {reqId && <RequestDrawer id={reqId} onClose={closeDrawer} onChanged={() => setRefreshKey((k) => k + 1)} />}
    </>
  );
}
